export interface WithIdName {
  id: number
  name: string
}

export interface WithIdLink {
  id: number
  link: string
}

export interface Paginated<T> {
  total: number
  items: T[]
}

export interface PaginatedWithBoosting<T> {
  filterTotal: number
  boostTotal: number
  items: T[]
}

export interface TotalWithItem<T> {
  total: number
  item: T
}

export interface BoostingWithItem<T> {
  filterTotal: number
  boostTotal: number
  item: T
}

export interface PrivateInfoDetectedResponse {
  hasPhone: boolean
  hasEmail: boolean
  hasLink: boolean
  isLinkAdded?: boolean
}

export interface PositionType extends WithIdNameParentId {
  priority: number
  isDeprecated: boolean
}

export interface WithIdNameParentId extends WithIdName {
  parentId: NumberOrNull
}

export type CareerType = WithIdName

export type TechStack = WithIdName

export type ServiceArea = WithIdNameParentId

export interface TechStackWithPositionTypes extends TechStack {
  positionTypes?: number[]
}

export interface Location extends WithIdNameParentId {
  priority: number
  isSeoul: boolean
  isRemote: boolean
}

export interface FundingRound extends WithIdName {
  devScoutDesc: string
}

export interface SchoolGroup extends WithIdName {
  description: string | null
}

export interface Banner {
  id: number
  desktop_img: string
  desktop_url: string
  mobile_img: string
  mobile_url: string
}

export interface User {
  id: number
  name: string
  email: string
  phone: string
  image: string
  experience: number
  interestedPositionTypes: PositionType[] // priority, isDeprecated 없음
  interestedTechStacks: TechStack[]
  acquisitionFunnel: string
  newMessageCount: number
  newInterestedStartupCount: number
  newCurationCount: number
  isActive: boolean
  isBirthYearOpen: boolean
  birthYear: string | null
  updatedByUserAt: string | null
}

export interface SearchedStartup extends WithIdName {
  thumbnail: string | null
  remarks: string | null
  proactivityScore: number
}

export interface AdminUser {
  id: number
  name: string
  image: string
}

export interface Startup extends WithIdName {
  thumbnail: string
  briefIntro: string //간단소개
  hasPt: boolean
  positionTypes: string[]
}

export interface FundingInfo {
  id: number
  round: string
  amount: number | null
  attractedAt: string | null
}

export interface StartupDetailForProposal extends WithIdName {
  thumbnail?: string
  briefIntro: string
  serviceAreas: string[]
  fundings: FundingInfo[] // 투자 단계
  memberCount: number
  devCount: number
  hasCto: boolean
  whatKindCompany: string // 어떤 회사인가요?
  curOutcome: string // 현재까지 성과
  members: StartupMember[]
  reasonForInvesting: string // 투자받은 이유
  images: string[]
}

export interface StartupMemberHistory {
  date: string
  total: number
  join: number
  leave: number
}

export interface StartupDetail extends StartupDetailForProposal {
  thumbnail: string
  tags: string[]
  effortForDev: string
  solvingProblem: string // 해결하는 문제
  culture: string
  howToStartup: string // 어떻게 창업하셨나요?
  pitch: string | null
  pt: string | null
  vision: string // 미래, 비전이 궁금해요
  lookForDev: string // 어떤 분을 찾고 계신가요?
  compensation: string // 보상
  welfare: string // 복지
  location: Location // 규격화된 위치 따로 받기 ex) 서울시 강남구 ~~~~ => 서울 강남
  address: string
  positions: PositionForStartupDetail[]
  isInterested: boolean
  businessNumber: number
  createdAt: string
  memberHistory: StartupMemberHistory[]
}

export interface MatchedStartup extends WithIdName {
  thumbnail: string
  briefIntro: string
  memberCount: number
  devCount: number
  hasCto: boolean
  fundingRound: string | null
  accumulatedFunding: number
  recentFundingDate: string | null
}

export interface StartupMember {
  id?: number
  name: string
  role: string
  career: string | null
  intro: string
}

export interface PositionCommon extends WithIdName {
  careerType: string
  experienceRange: Range | null
  techStacks: string[]
  publishedAt: string // 포지션 공개처리 일시
  createdAt: string // 이걸로 new 인지 판단
  isInterested: boolean
}

export interface PositionForList extends PositionCommon {
  averageReplyPeriod: NumberOrNull
  isSincerityReview: boolean
  hasCuration: boolean
  isActivelyHireAds: boolean
  matchRate: number
}

export interface PositionForStartupDetail extends PositionCommon {
  preferred: string
  hiringProcess: string
  qualification: string
  task: string
  hasSupervisor: boolean
  expectationKeywords: WithIdName[]
}

export interface StartupSimple extends WithIdName {
  thumbnail: string
  serviceAreas: string[]
  tags: string[]
  briefIntro: string
  location: string
  memberCount: number
  devCount: number
  hasCto: boolean
  address: string
}

export interface StartupForSearch extends StartupSimple {
  pt: string | null
  pitch: string | null
  isNew: boolean // 읽지 않은 "나에게 관심있는 스타트업" 여부
  viewedMeAt: string | null
  savedMeAt: string | null
  createdAt: string
}

export interface UserApplicationForPosition {
  isPossibleApply: boolean
  applyAvailableDate: string | null
  lastApplication: {
    resumeAcceptedAt: string | null
    acceptedAt: string | null
  } | null
  hasTempApplication: boolean
}

export interface AccountMeInterestedPosition {
  startup: {
    id: number
    name: string
    thumbnail: string
    startupPosition: WithIdName & { isInterested: boolean; careerType: string }
  }
}

export interface StartupPositionForSearch extends PositionForList {
  startup: StartupForSearch
  userApplication: UserApplicationForPosition
  matchedKeys: StartupPositionFilterItemKey[]
}

export interface StartupPositionForSearchNoRequirePosition
  extends Partial<PositionForList> {
  startup: StartupForSearch
  userApplication: UserApplicationForPosition | null // 이때는 포지션이 없을 수도 있으므로 예외적으로 null 가능
  matchedKeys?: StartupPositionFilterItemKey[]
}

export interface StartupPositionDetail extends PositionForStartupDetail {
  positionTypes: PositionType[]
  mainPositionTypeId: NumberOrNull
  location: WithIdName | null
  address: string
  internPeriod: NumberOrNull
  jobInterestLevel: NumberOrNull
  remoteWorkPreference: StartupPositionRemoteWorkType
  startup: StartupSimple
  userApplication: UserApplicationForPosition
  updatedAt: string
  averageReplyPeriod: NumberOrNull
  isSincerityReview: boolean
}

export interface BaseInfo {
  banners: Banner[]
  careerTypes: CareerType[]
  positionTypes: PositionType[]
  techStacks: TechStackWithPositionTypes[]
  locations: Location[]
  rounds: FundingRound[]
  idpCertificates: IdpCertificate[]
  expectationKeywords: ExpectationKeyword[]
  expectationCategories: WithIdName[]
}

export interface StartupSearchingReqParams extends PaginationParams {
  searchQuery?: string
  isPublished?: boolean
}

export interface StartupPositionFilterParams {
  parentPositionTypes?: number[] // 앞으로 확장성을 위해 배열이지만, 지금은 id 한개만 담음
  positionTypes?: number[] // id
  careerTypes?: number[] // id
  locations?: number[]
  parentLocations?: number[]
  serviceAreas?: number[]
  techStacks?: number[]
  minExperience?: number
  maxExperience?: number
  jobInterestLevels?: number[]
  minMemberCount?: number
  maxMemberCount?: number
  fundingRounds?: number[]
  expectationKeywords?: number[]
  hasSupervisor?: boolean
  hasCto?: boolean
  remoteWorkPreference?: StartupPositionRemoteWorkType[]
  minSalary?: number
  stockOption?: StartupPositionStockOptionType[]
  isViewed?: boolean // 조회 여부
  minMatchRate?: number // 최소 매칭률
}

export interface StartupPositionReqParams extends StartupPositionFilterParams {
  offset: number
  limit: number
  orderBy?: StartupPositionOrderByType
}

export type StartupPositionOrderByType = 'replyPeriod' | '-updatedAt'

export interface LoginResult {
  needInquiry: boolean
  fromSite: string
  tempToken?: string
  name?: string
  email?: string
  phone?: string
  image?: string
}

export interface StartupPage {
  curr: StartupDetail
}

export interface Envelope<T = unknown> {
  status: number
  data?: T
  msg?: string
}

export interface ApplicationStatus {
  id: number
  name: ApplicationStatusNameType
  count: number
}

export type ApplicationStatusNameType =
  | '지원자'
  | '서류 합격'
  | '최종 합격'
  | '불합격'
  | '지원서 마감'
  | '불합격 예약'

export interface DashboardApplicationSummary {
  position: {
    id: number
    name: string
    careerType: WithIdName | null
    isPublished: boolean
  }
  appliedCount: number
  newAppliedCount: number
  approachingAutoRejectCount: number
  newPreInterviewResolvedCount: number
  status: ApplicationStatus[]
}

export interface ApplicationCommon {
  id: number
  coverLetter: string | null
  joinReason: string | null
  fitReason: string | null
  fulfillments: ApplicationFulfillment
  position: { id: number; name: string; careerType: string }
  statusId: number
  appliedAt: string
  acceptedAt: string | null
  rejectedAt: string | null
  resumeAcceptedAt: string | null
}

export interface ApplicationDetail extends ApplicationCommon {
  startup: { id: number; name: string; thumbnail: string }
  rejectMessage: string | null
  canceledAt: string | null
  lastViewedAt: string | null
  firstViewedAt: string | null
}

export interface PreInterview {
  status: 'TEMP_SAVED' | 'REQUESTED' | 'RESOLVED' | 'FAILED'
  requestedAt: string | null
  resolvedAt: string | null
  failedAt: string | null
  failReason: string | null
  lastViewedAt: string | null
}

export interface DashboardApplicationDetail extends ApplicationCommon {
  user: {
    name: string
    phone: string
    email: string
    image: string | null
    experience: number
    positions: PositionType[]
  }
  files: AttachmentWithAccessibility[]
  links: AttachmentWithAccessibility[]
  dueDate: string
  isExtendedDueDate: boolean
  scheduledRejectionDate: string | null
  chatStep: DashboardChatStatusId
  preInterview: PreInterview | null
}

interface ApplicationItemCommon {
  unreadMessagesCount: number
  lastMessage: ChatMessage | null
}

export interface ApplicationItem
  extends ApplicationCommon,
    ApplicationItemCommon {
  startup: { id: number; name: string; thumbnail: string }
  files: WithIdLink[]
  links: WithIdLink[]
  rejectMessage: string | null
  canceledAt: string | null
  lastViewedAt: string | null
  firstViewedAt: string | null
}

export interface DashboardApplicationItem
  extends DashboardApplicationDetail,
    ApplicationItemCommon {
  scoutRequest?: DashboardScoutProfileItem // isFromChat 아닐 때(지원자 관리)
  scoutRequestId?: number // isFromChat 일 때(채팅)
}

export interface TempApplication extends WithIdName {
  files: WithIdLink[]
  links: WithIdLink[]
  coverLetter: string | null
  fitReason: string | null
  joinReason: string | null
  scoutRequestId: number
  appliedAt: string
  fulfillments: ApplicationFulfillment | null
  hasNewConditions: boolean
}
export interface StartupPositionApplicationGenerate {
  id: number
  fitReason: string | null
  joinReason: string | null
  fulfillments: ApplicationFulfillment | null
}

export interface ApplicationFulfillment {
  task: ApplicationFulfillmentCondition[]
  preferred: ApplicationFulfillmentCondition[]
  qualification: ApplicationFulfillmentCondition[]
}

export interface ApplicationFulfillmentCondition
  extends ApplicationFulfillmentParams {
  condition: string
}

export interface ApplicationsReqParams {
  applicationStatuses: number[]
  offset: number
  limit: number
  saveLists?: number[]
  isResumeAcceptExists?: boolean // 서류합격 후 불합격 된 데이터만 내려줌
  isFromChat?: boolean // scout request를 안내려줌
  chatStep?: DashboardChatStatusId
  userName?: string
  positionId?: number
}

export interface ScoutProposalDetailCommon extends WithIdName {
  fulfillments: ScoutFulfillment[]
  message: string
  isAutoRejected: boolean
  isRejected: boolean | null
  isCanceled: boolean
  isResumeRequest: boolean
  isResumeAllowed: boolean
  dueDate: string
  proposedAt: string
  status: ScoutProposalStatus
}

export interface DashboardScoutProposalDetail
  extends ScoutProposalDetailCommon {
  user: {
    name: string
    phone?: string // isAdmin일 경우만 내려줌
    email?: string // isAdmin일 경우만 내려줌
  }
  lastViewedAt: string | null
  chatStep: DashboardChatStatusId
  acceptedAt: string | null
  relatedPerStartupCurations?: AdminRelatedPerStartupCuration[] // 구직도우미 전용
}

export interface AdminRelatedPerStartupCuration {
  startup: WithIdName
  perStartupCuration: WithIdName
  perStartupCurationScoutRequestId: number
  // 추천 상태
  curatedAt: string // 추천일
  lastViewedAt: string | null // 프로필 조회
  proposedAt: string | null // 제안함
  rejectedAt: string | null // 불합격
  acceptedAt: string | null // 최종합격
  resumeAcceptedAt: string | null // 서류합격
  uninterestedAt: string | null // 관심 없음
  brokingRequestedAt: string | null // 연결 요청
  brokingRequestedResolvedAt: string | null // 연결 요청 처리 일시 (메시지를 보내지 않는 것이 처리 결과일 수도 있음)
  shouldBrokingScoutProposal: boolean // 직접 연결 필요 여부
  // 응답 상태
  brokingRequestStatus: BrokingRequestStatus
  brokingRequestStatusUpdatedAt: string | null
  brokingRequestStatusUpdatedBy: string | null // 처리한 구직도우미 계정의 username
}

export interface ScoutProposalDetail extends ScoutProposalDetailCommon {
  positions: PositionCommon[]
}

export enum ChatStatus {
  NOT_RESPONSED = '미응답',
  NOT_ACCEPTED = '채팅 수락전',
  CHATTING = '채팅 진행중',
  MEET_CONFIRMED = '커피챗 예정',
  MEET_DONE = '커피챗 완료',
  ENDED = '채팅 종료',
  RESUME_ACCEPTED = '서류 합격',
  ACCEPTED = '최종 합격',
  REJECTED = '불합격',
}

export interface StartupUser {
  id: number // 이건 startup user 에 관한 id 임
  username: string
  startupId: number
  createdAt: string
  isPublished: boolean
  isSigned: boolean
  isLocked: boolean
  banReason: string | null // 이용정지 되지 않은 경우 null
  isAdmin: boolean
  hasPass: boolean // 무한패스 여부
  showApplicantContactInfo: boolean // 스펙터 측 요청 -> 공고 지원자 중 지원자 / 서류합격자 / 최종합격자 상태에 대해서만 지원서, pdf 에 전화번호와 이메일 공개
  totalTempProposals: number
  isInfoFillRequest: boolean
  shouldAutoRemindProposalPendingTalent: boolean
  isPublishedStartupPosition: boolean
  pendingProposalCount: number
  newInterestedUserCount: number
  newSavedUserCount: number
  newStartupPositionScoutSearch: Omit<DashboardScoutsSearchInfo, 'isNew'>
  curationUpdate: {
    newCuratedScoutRequestCount: number
    newAcceptanceCount: number
    recentCurationId: number | null
    managerName: string | null // recentCurationId 가 있을 때만 값이 있을 수 있음
    managerImage: string | null // recentCurationId 가 있을 때만 값이 있을 수 있음
  } | null
  needChatStepCleanUp: boolean
  agreements: HirePenaltyAgreements
  isAutoProposalGenerationEnabled: boolean
  isPreInterviewContractSigned: boolean
  isPreInterviewContractSent: boolean
  showScoutWizard: boolean
}

interface HirePenaltyAgreements {
  agreedPenaltyForHireAfterCancellation: boolean //제안 취소 후 채용 미고지 불이익 동의
  agreedPenaltyForHireAfterRejection: boolean //불합격 후 채용 미고지 불이익 동의
  agreedPenaltyWhenIntentionalHiding: boolean //합격 후 채용 사실 의도적 미고지 불이익 동의)
  agreedHireNotification: boolean //채용 후 고지 동의
}

export type AlertItemType =
  | 'weeklyReport'
  | 'applicationApplied'
  | 'applicationAutoRejected'
  | 'applicationCanceled'
  | 'proposalAccepted'
  | 'proposalRejected'
  | 'newMessage'
  | 'unreadMessage'
  | 'meetScheduleRemind'
  | 'hireStatusUpdate'
  | 'positionUnpublished'
  | 'talentRecommendationForPosition'
  | 'publishedNewCuration'
  | 'processCurationRemind'
  | 'rejectedCurationScoutRequest'

export type NotificationChannelSettings = {
  isPhoneNoti: boolean
  isEmailNoti: boolean
}

export type AlertSettings = {
  [key in AlertItemType]: NotificationChannelSettings
}

export interface StartupPIC extends WithIdName {
  position: string
  phone: string | null
  email: string | null
  isStopAlertLateTime: boolean
  alertSettings: AlertSettings
}

export interface AlertSettingsPayload {
  alertType: AlertItemType
  isPhoneNoti?: boolean
  isEmailNoti?: boolean
}

export interface DashboardPosition {
  id: number
  name: string
  positionTypes: PositionType[]
  mainPositionTypeId: number | null
  careerType: WithIdName | null
  experienceRange: Range | null
  techStacks: WithIdName[]
  hasSupervisor: boolean
  task: string
  qualification: string
  preferred: string
  hiringProcess: string
  expectationKeywords: WithIdName[]
  isPublished: boolean
  publishedAt: string | null
  applicationCount: number
  createdAt: string
  talentRecommendRequestedAt: string | null
  jobInterestLevel: number | null
  location: WithIdNameParentId | null
  address: string
  minDesiredSalary: number
  maxDesiredSalary: number
  maxNegotiableSalary: number
  internPeriod: number | null
  stockOption: StartupPositionStockOptionType | null
  remoteWorkPreference: StartupPositionRemoteWorkType | null
  isSameLocation: boolean | null
}

export enum StartupPositionRemoteWorkEnum {
  FULL_REMOTE_NECESSARY = '100% 원격근무 가능',
  PART_REMOTE_NECESSARY = '부분 원격근무 가능',
  NOT_ALLOWED = '원격근무 없음',
}

export type StartupPositionRemoteWorkType =
  keyof typeof StartupPositionRemoteWorkEnum

export enum StockOptionEnum {
  NOT_OFFERED = '아뇨, 제공하지 않아요',
  OFFERED = '네, 스톡옵션을 제공해요',
  NEGOTIABLE = '협의해볼 수 있어요',
}

export type StartupPositionStockOptionType = keyof typeof StockOptionEnum

export interface DashboardPositionParams {
  name: string
  positionTypes: number[]
  mainPositionTypeId: number | null
  careerType: number | null
  experienceRange: Range | null
  techStacks: number[]
  hasSupervisor: boolean
  task: string
  qualification: string
  preferred: string
  hiringProcess: string
  expectationKeywords: number[]
  internPeriod: number | null
  jobInterestLevel: number | null
  remoteWorkPreference: string | null
  minDesiredSalary: number
  maxDesiredSalary: number
  maxNegotiableSalary: number
  stockOption: string | null
  location: number | null
  address: string
  isSameLocation: boolean | null
}

export interface DashboardFundingInfo {
  round: number // id 투자 단계
  amount: number // 투자 유치액
  attractedAt: string // 투자 유치 날짜
  isHideAmount: boolean
}

export interface DashboardStartupDetail {
  id?: number
  name: string | null
  pitch: string | null
  pt: string | null
  thumbnail: string | File | null
  briefIntro: string | null
  serviceAreas: ServiceArea[]
  tags: string[]
  hasInvestmentInfo: boolean
  funding: DashboardFundingInfo[]
  reasonForInvesting: string | null // 투자받은 이유
  effortForDev: string | null // 개발자를 위한 노력
  solvingProblem: string | null // 해결하는 문제
  address: string | null
  location: NumberOrNull //id
  memberCount: NumberOrNull
  devCount: NumberOrNull
  compensation: string | null // 보상
  welfare: string | null // 복지
  images: (string | File)[]
  howToStartup: string | null // 어떻게 창업하셨나요?
  whatKindCompany: string | null // 어떤 회사인가요?
  curOutcome: string | null // 현재까지 성과
  vision: string | null // 미래, 비전이 궁금해요
  lookForDev: string | null // 어떤 분을 찾고 계신가요?
  culture: string | null // 조직문화
  members: StartupMember[]
  hasCto: boolean
  isPublished: boolean
  publishedAt: string
  updatedAt: string
}

const accountAlertSettingKeys = [
  'shouldAlertGeneralService', // 핵심 서비스 이용 알림 (기본값 true)
  'shouldAlertInterestedStartupUpdate', // 관심 스타트업 업데이트 알림
  'shouldAlertFitCuratedPosition', // 커리어에 딱 맞는 채용 공고 알림 (기본값 true)
  'shouldAlertGeneralServiceViaEmail', // 이메일 알림 (기본값 true)
  'shouldAlertNewFitStartup', // 신규 채용 공고 알림
  'shouldAlertStartupsInterestedInMe', // 나에게 관심 있는 스타트업 알림 (기본값 true)
  'shouldAlertPromotion', // 이벤트 및 혜택 알림
] as const

export type AccountAlertSettingKey = typeof accountAlertSettingKeys[number]

export type AccountAlertSetting = Record<AccountAlertSettingKey, boolean>

export interface WorkLevel extends WithIdName {
  description: string
}

export interface JobInterestLevel {
  id: number
  description: string
  workLevelId: number
}

export interface ExpectationKeyword extends WithIdName {
  categoryId: number
}

export interface RequestFormOptions {
  workLevels: WorkLevel[]
  educationCategories: WithIdName[]
  jobInterestLevels: JobInterestLevel[]
  locations: Location[]
  serviceAreas: ServiceArea[]
  fundingRounds: FundingRound[]
  educationStatus: WithIdName[]
  expectationCategories: WithIdName[]
  expectationKeywords: ExpectationKeyword[]
  languages: WithIdName[]
  languageLevels: WithIdName[]
  personalities: WithIdName[]
}

export interface Range {
  min: number
  max: number
}

export type ScoutRequestFormDataKey =
  | 'locations'
  | 'serviceAreas'
  | 'fundingRounds'
  | 'memberCount'
  | 'devCount'
  | 'salary'
  | 'shouldSupervisor'
  | 'shouldCto'
  | 'shouldStockOption'
  | 'remoteWorkPreference'

export type ScoutRequestDataKeyIsRequired = {
  dataKey: ScoutRequestFormDataKey
  isRequired: boolean
}

export type StartupPositionFilterItemKey =
  | Exclude<ScoutRequestFormDataKey, 'devCount'> // devCount는 필터에 없어서 제외
  | 'positionTypes'
  | 'techStacks'
  | 'experience'
  | 'jobInterestLevel'
  | 'expectationKeywords'

export interface StartupPositionFilterItem {
  id: StartupPositionFilterItemKey
  name: string
}

export type School = WithIdNameImage & {
  groups: number[] | null
}

export interface EducationHistory {
  id?: number
  status: WithIdName | null
  isCs: boolean
  major: string
  category: WithIdName | null
  admissionYear: string | null
  graduationYear: string | null
  school: School
  isCreatedByUser: boolean
  isCompleted?: boolean
  isEdited?: boolean
}

// Company 모델의 모든 필드를 포함한 타입
export interface Company extends WithIdName {
  image: string | null
  isStartup: boolean
  serviceAreas: ServiceArea[]
  isCreatedByUser: boolean
  businessRegistrationNumber: string | null
}

// TODO: 기업 사이드에는 isCreatedByUser가 없음 -> 헷갈려서 실수할 수 있으니 타입 정리 or api 응답 통일 필요
export type CompanyForCareerHistory = Omit<
  Company,
  'businessRegistrationNumber' | 'serviceAreas'
>

export interface CareerHistory {
  id?: number
  company: CompanyForCareerHistory
  link: string | null
  position: string
  started: string
  ended: string | null
  isWorking: boolean
  techStacks: TechStack[]
  serviceAreas: ServiceArea[]
  description: string | null
  isVerified: boolean
}

export interface LanguageSkill {
  id: number
  language: WithIdName
  level: WithIdName
  description: string
}

export interface OtherActivityHistory {
  id?: number
  title: string
  description: string
  started: string | null
  ended: string | null
  techStacks: TechStack[]
  isOnGoing: boolean
}

export interface AttachmentWithAccessibility extends WithIdLink {
  isAccessible: boolean
}

export interface ScoutRequestForExample extends WithIdName {
  positions: WithIdName[]
  techStacks: WithIdName[]
  workLevel: WithIdName | null
  jobInterestLevel: NumberOrNull
  isActivelySeeker: boolean
  idpCertificates: number[]
  educations: EducationHistory[] // 스카우트 프로필 공개가 아니라 지원한 경우 값이 없을 수 있음
  languageSkills: LanguageSkill[]
  experience: number
  careers: CareerHistory[]
  otherActivities: OtherActivityHistory[]
  introduction: string | null
  personalities: WithIdName[]
  expectation: { description: string; keywords: WithIdName[] }
  jobSearchingReason: string | null
}

export interface DashboardScoutRequestDetail extends ScoutRequestForExample {
  birthYear: string | null
  image: string
  summary: { title: string; content: string }[] | null
  memberCount: Range | null // 구직 조건의 경우 지원자 프로필에서 보여주지 않음
  devCount: Range | null
  salary: Range | null
  locations: Location[] | null
  serviceAreas: WithIdName[] | null
  fundingRounds: WithIdName[] | null
  miscOptions: ScoutRequestMiscOptions | null
  requiredConditions: ScoutRequestDataKeyIsRequired[] | null
  files: AttachmentWithAccessibility[]
  links: AttachmentWithAccessibility[]
  updatedAt: string
  assessMemo: string | null
  saveLists: number[]
  lastActiveAt: string | null
  viewedAt: string | null
  viewedMeAt: string | null
  lastListedAt: string | null
  savedMeAt: string | null
  proposalId: NumberOrNull
  isProposed: boolean
  isCanceled: boolean
  isRejected: boolean | null
  isResumeAllowed: boolean
  startupDismissed: boolean
  status: ScoutProposalStatus | null
  hasPendingScoutProposal: boolean
  hasTempScoutProposal: boolean
  hasConfirmedMeet: boolean
  hasDoneMeet: boolean
  hasApplication: boolean
  respondedCount: number
  receivedMessageCount: number
  isCommonCurated: boolean
  managerComment: ManagerComment | null
  curatedAt: string | null
  isDeleted: boolean
  isPublished: boolean
  commonComment: ManagerComment | null
  aiAssessment?: ScoutRequestAiAssessment | null
  filterAssessment?: ScoutRequestFilterAssessment | null
  assessmentScore?: number
  resultType?: 'filter' | 'boost'
}

export interface ScoutRequestMiscOptions {
  shouldCto: boolean
  shouldSupervisor: boolean
  shouldStockOption: boolean
  isSalaryNegotiableWithStockOption: boolean
  remoteWorkPreference: RemoteWorkType
}

export type RemoteWorkType =
  | 'PART_REMOTE_NECESSARY'
  | 'FULL_REMOTE_NECESSARY'
  | null

export interface ConditionForm {
  workLevel: NumberOrNull // 구직조건 선택하기 전에 프로필이 생길 수 있어서 null 가능해짐
  jobInterestLevel: NumberOrNull
  isActivelySeeker: boolean
  locations: WithIdNameParentId[]
  serviceAreas: number[]
  fundingRounds: number[]
  memberCount: Range
  devCount: Range
  salary: Range
  miscOptions: ScoutRequestMiscOptions
  requiredConditions: ScoutRequestDataKeyIsRequired[]
  lastSavedStep: number // 중도 이탈 시 나중에 돌아오면 다시 시작할 페이지. 저장하지 않으면 0
}

export interface BlockedCompany {
  startupId: number | null // 입점사인 경우만
  companyId: number | null // 미입점사인 경우만
  name: string
}

export interface ScoutProfile extends ConditionForm {
  id: number
  name: string
  image: string | File
  educations: EducationHistory[]
  languageSkills: LanguageSkill[]
  experience: number
  careers: CareerHistory[]
  otherActivities: OtherActivityHistory[]
  files: WithIdLink[]
  links: WithIdLink[]
  introduction: string
  personalities: WithIdName[]
  expectation: { description: string; keywords: WithIdName[] }
  jobSearchingReason: string | null
  isPublished: boolean
  idpCertificates: IdpCertificateStatus[]
  idpScore: number
  idpGrade: IdpGrade
  exposureRankingRatio: number
  blockedCompanies: BlockedCompany[]
  updatedByUserAt: string
  publishedAt: string | null
  createdAt: string
  hasAnalyzedResult: boolean
}

export interface ScoutProfileDrafts {
  expectations: string[]
  introductions: string[]
  jobSearchingReasons: string[]
  careers: CareerHistory[]
  educations: EducationHistory[]
  languageSkills: LanguageSkill[]
  otherActivities: OtherActivityHistory[]
}
// career 저장/수정하는 PUT 요청할 때 body 파라미터
export interface ScoutProfileCareerPayload {
  id?: number
  name: string
  company: number | null
  serviceAreas: number[]
  isStartup: boolean
  link: string | null
  position: string
  started: string
  ended: string
  isWorking: boolean
  techStacks: number[]
  description: string
  isVerified: boolean
}

export interface OtherActivityHistoryPayload
  extends Omit<OtherActivityHistory, 'techStacks'> {
  techStacks: number[]
}

export interface LanguageSkillPayload {
  language: number
  level: number
  description: string
}

export interface EducationPayload
  extends Omit<
    EditableEducation,
    'status' | 'category' | 'image' | 'isCreatedByUser'
  > {
  category: number | null
  status: number
}

export interface ScoutRequestParams extends Omit<ConditionForm, 'locations'> {
  locations: number[]
  addedEducations: EducationPayload[]
  removedEducations: number[]
  addedLanguageSkills: LanguageSkillPayload[]
  removedLanguageSkills: number[]
  addedCareers: ScoutProfileCareerPayload[]
  removedCareers: number[]
  addedOtherActivities: OtherActivityHistoryPayload[]
  removedOtherActivities: number[]
  files: number[]
  addedLinks: string[]
  removedLinks: number[]
  introduction: string
  personalities: number[]
  expectation: { description: string; keywords: number[] }
  jobSearchingReason: string
}

export interface VerifiedCareer {
  name: string
  started: string
  ended: string
  companyId: number | null
}

export interface VerifyCareerRequestHistory {
  dailyRequestCount: number
  completedAt: string | null
  failedAt: string | null
  careers: VerifiedCareer[] | null // null인 경우는 요청이 실패한 경우
}

export type IdpGrade = 1 | 2 | 3 | 4 | 5

export interface IdpCertificateStatus {
  id: number
  isCertified: boolean | null
  isContentFilled: boolean
}

export interface Condition {
  dataKey: ScoutRequestFormDataKey
  value: string
}

export interface ScoutPosition extends WithIdName {
  careerType: string
  techStacks: string[]
}

export enum WebSocketMessageType {
  WRITE = 'chat.message.write',
  READ = 'chat.message.read',
  LIST = 'chat.message.list',
}

export interface ApplicationsParams {
  applicationStatuses: number[]
  isResumeAcceptExists?: boolean
}

export enum ChatMessageType {
  INITIAL = 'initial',
  AUTO_REMIND = 'autoRemind',
  NORMAL = 'normal',
  FILE = 'file',
  CANCEL = 'cancel',
  REJECT = 'reject',
  ACCEPT = 'accept',
  RESUME_REQUEST = 'resumeRequest',
  RESUME_ALLOWED = 'resumeAllowed',
  MEET_DESIRE = 'meetDesire',
  MEET_PROPOSE = 'meetPropose',
  MEET_CONFIRM = 'meetConfirm',
  APPLICATION_RESUME_ACCEPT = 'applicationResumeAccept',
  APPLICATION_APPLY = 'applicationApply',
  APPLICATION_REJECT = 'applicationReject',
  APPLICATION_ACCEPT = 'applicationAccept',
  SCOUT_REQUEST_INFO_FILL_REQUEST = 'scoutRequestInfoFillRequest',
  BROKING_SCOUT_PROPOSAL = 'brokingScoutProposal',
  BROKING_SCOUT_PROPOSAL_ACCEPT = 'brokingScoutProposalAccept',
  BROKING_SCOUT_PROPOSAL_REJECT = 'brokingScoutProposalReject',
  HIRE_TRACKING = 'hireTracking',
  ASK_SEEKING_JOB = 'askSeekingJob',
  ASK_SCHEDULE_MEETING = 'askScheduleMeeting',
  ASK_PREFERRED_MEETING = 'askPreferredMeeting',
  END_OF_SEEKING_JOB = 'endOfSeekingJob',
  ASK_PREFERRED_OFFER = 'askPreferredOffer',
}

export interface BaseChatMessage {
  type: WebSocketMessageType.WRITE
  proposalId: NumberOrNull
  applicationId: NumberOrNull
  messageId: number
  startupId: NumberOrNull
  userId: NumberOrNull
  sentAt: string
  readAt: string | null
}

export interface AskPreferredOfferMessage extends BaseChatMessage {
  messageType: ChatMessageType.ASK_PREFERRED_OFFER
  message: string
  perStartupCurationScoutRequestId: number
  startupName: string
  isButtonDisabled: boolean
}

export interface EndOfSeekingJobMessage extends BaseChatMessage {
  messageType: ChatMessageType.END_OF_SEEKING_JOB
  message: string
  perStartupCurationScoutRequestId: number
  startupName: string
  isButtonDisabled: boolean
}

export interface AskPreferredMeetingTypeMessage extends BaseChatMessage {
  messageType: ChatMessageType.ASK_PREFERRED_MEETING
  message: string
  perStartupCurationScoutRequestId: number
  startupName: string
  isButtonDisabled: boolean
}

export interface AskScheduleMeetingMessage extends BaseChatMessage {
  messageType: ChatMessageType.ASK_SCHEDULE_MEETING
  message: string
  perStartupCurationScoutRequestId: number
  startupName: string
  isButtonDisabled: boolean
}

export interface AskSeekingJobMessage extends BaseChatMessage {
  messageType: ChatMessageType.ASK_SEEKING_JOB
  message: string
  perStartupCurationScoutRequestId: number
  startupName: string
  isButtonDisabled: boolean
}

export interface ApplicationAcceptChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.APPLICATION_ACCEPT
}

export interface ApplicationRejectChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.APPLICATION_REJECT
  message: string
}

export interface InitialChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.INITIAL
}

export interface ApplicationApplyChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.APPLICATION_APPLY
  message: string
}

export interface ApplicationResumeAcceptChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.APPLICATION_RESUME_ACCEPT
  message: string
}

export interface NormalChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.NORMAL
  message: string
}

export interface AutoRemindChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.AUTO_REMIND
  message: string
}

export interface FileChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.FILE
  file: AttachmentWithAccessibility
}

export interface CancelChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.CANCEL
  message: string
}

export interface RejectChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.REJECT
  message: string
}

export interface AcceptChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.ACCEPT
}

export interface ResumeRequestChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.RESUME_REQUEST
  isResumeAllowed: boolean
}

export interface ResumeAllowedChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.RESUME_ALLOWED
}

export interface ScoutRequestInfoFillRequestChatMessage
  extends BaseChatMessage {
  messageType: ChatMessageType.SCOUT_REQUEST_INFO_FILL_REQUEST
  message: string
}

export interface BrokingScoutProposalChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.BROKING_SCOUT_PROPOSAL
  message: string
  perStartupCurationScoutRequestId: number
  isButtonDisabled: boolean
}

export interface BrokingScoutProposalAcceptChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.BROKING_SCOUT_PROPOSAL_ACCEPT
  message: string
  startupName: string
}

export interface BrokingScoutProposalRejectChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.BROKING_SCOUT_PROPOSAL_REJECT
  message: string
  startupName: string
}

export interface HireTrackingMessage extends BaseChatMessage {
  messageType: ChatMessageType.HIRE_TRACKING
  message: string
}

export interface MeetDesire {
  meetDesireId: number
  isReplied: boolean
  preferDates: string[]
  preferOffline: boolean
  preferOnline: boolean
  isAlreadyConfirmed: boolean
}

export interface MeetConfirm {
  date: string
  isOffline: boolean
  isOnline: boolean
  offlineAddress: string | null
  onlineLink: string | null
}

export interface MeetDesireChatMessage extends BaseChatMessage {
  messageType: ChatMessageType.MEET_DESIRE
  meetDesire: MeetDesire
}

export interface MeetProposeMessage extends BaseChatMessage {
  messageType: ChatMessageType.MEET_PROPOSE
  meetPropose: {
    id: number
    officeAddress: string
    isReplied: boolean
  }
}

export interface MeetConfirmMessage extends BaseChatMessage {
  messageType: ChatMessageType.MEET_CONFIRM
  meetConfirm: MeetConfirm
}

export type ChatMessage =
  | InitialChatMessage
  | AutoRemindChatMessage
  | NormalChatMessage
  | FileChatMessage
  | CancelChatMessage
  | RejectChatMessage
  | AcceptChatMessage
  | ResumeRequestChatMessage
  | ResumeAllowedChatMessage
  | MeetDesireChatMessage
  | MeetProposeMessage
  | MeetConfirmMessage
  | ApplicationResumeAcceptChatMessage
  | ApplicationApplyChatMessage
  | ApplicationRejectChatMessage
  | ApplicationAcceptChatMessage
  | ScoutRequestInfoFillRequestChatMessage
  | BrokingScoutProposalChatMessage
  | BrokingScoutProposalAcceptChatMessage
  | BrokingScoutProposalRejectChatMessage
  | HireTrackingMessage
  | AskSeekingJobMessage
  | AskScheduleMeetingMessage
  | AskPreferredMeetingTypeMessage
  | EndOfSeekingJobMessage
  | AskPreferredOfferMessage

export interface Chat {
  lastMessage: ChatMessage | null
  unreadMessagesCount: number
  isBlocked?: boolean
}

export interface ChatData {
  type: WebSocketMessageType
  proposalId: NumberOrNull
  applicationId: NumberOrNull
}

export interface MessageList extends ChatData {
  messages: ChatMessage[]
}

export interface ReadMessage extends ChatData {
  messageId: number
}

export interface ScoutProposalCommon {
  name: string
  requestId: number
  proposalId: number
  dueDate: string
  isCanceled: boolean
  isRejected: boolean
  hasConfirmedMeet: boolean
  hasDoneMeet: boolean
  lastMessage: ChatMessage
  status: ScoutProposalStatus
  unreadMessagesCount: number
  proposedAt: string
  acceptedAt: string | null
}

export interface ScoutProposal extends ScoutProposalCommon {
  startupId: number
  thumbnail: string
}

export interface DashboardScoutProposal extends ScoutProposalCommon {
  profileImage: string
  chatStep: DashboardChatStatusId
  shouldBrokingScoutProposal?: boolean // 구직도우미 전용
}

export type DashboardScoutProposalMessageCreationMode =
  | 'AUTO'
  | 'PREVIOUS'
  | 'CUSTOM'

export interface DashboardScoutProposalSettings {
  messageCreationMode: DashboardScoutProposalMessageCreationMode
  isRelatedStartupPositionAutoSelectionEnabled: boolean
  isMatchingRequirementAutoSelectionEnabled: boolean
  created: boolean
}

export interface ScoutFulfillment {
  dataKey: ScoutRequestFormDataKey
  description: null | string
}

export interface DashboardTempScoutProposal {
  appealPoints: string
  fulfillments: ScoutFulfillment[]
  introPerson: string
  outro: string
  positions: number[]
  proposeReason: string
  isResumeRequest: boolean
}

export interface DashboardInitialScoutProposal {
  fulfillments: ScoutFulfillment[]
  introPerson: string
  outro: string
  proposeReason?: string
  appealPoints?: string
}

export interface DashboardAutoPosition {
  position: NumberOrNull
}

export interface DesireMeetParams {
  preferDates: string[]
  preferOnline: boolean
  preferOffline: boolean
  meetProposeId: NumberOrNull
}

export interface DashboardSimpleScoutProfile extends WithIdName {
  birthYear: string | null
  image: string
  isActivelySeeker: boolean
  jobInterestLevel: NumberOrNull
  workLevel: WithIdName | null
  techStacks: WithIdName[]
  positions: WithIdName[]
  careers: CareerHistory[]
  otherActivities: OtherActivityHistory[]
  experience: number
  educations: EducationHistory[]
  introduction: string | null
  idpCertificates: number[]
  viewedAt: string | null // 기업이 인재 확인
  receivedMessageCount: number
  respondedCount: number
  assessMemo?: string
  summary: { title: string; content: string }[] | null
  isCommonCurated: boolean
  managerComment: ManagerComment | null
}

export interface ManagerComment {
  manager: {
    id: number
    name: string
    image: string
  } | null
  comment: string
}

export interface DashboardScoutProfileItem extends DashboardSimpleScoutProfile {
  hasTempScoutProposal: boolean
  hasApplication: boolean
  saveLists: number[]
  isProposed: boolean
  hasConfirmedMeet: boolean
  hasDoneMeet: boolean
  isCanceled: boolean
  isRejected: boolean
  startupDismissed: boolean
  proposalId?: number
  updatedAt: string
  lastActiveAt: string | null
  lastListedAt: string | null
  viewedMeAt: string | null // 인재가 기업 확인
  savedMeAt: string | null // 인재가 기업 저장
  hasPendingScoutProposal: boolean
  isNew?: boolean
  requiredConditions: ScoutRequestDataKeyIsRequired[] | null
  locations: Location[] | null
  serviceAreas: WithIdName[] | null
  fundingRounds: WithIdName[] | null
  memberCount: Range | null
  devCount: Range | null
  salary: Range | null
  miscOptions: ScoutRequestMiscOptions | null
  aiAssessment?: ScoutRequestAiAssessment | null
  filterAssessment?: ScoutRequestFilterAssessment | null
  assessmentScore?: number
  resultType?: 'filter' | 'boost'
}

export interface DashboardProposedScoutProfileItem
  extends DashboardScoutProfileItem {
  proposedAt: string
}

export type DashboardCuratedScoutRequest = DashboardScoutProfileItem &
  CurationStatus

export interface DashboardCuratedScoutsRequests {
  newCurated: DashboardCuratedScoutRequest[]
  waiting: DashboardCuratedScoutRequest[]
  accepted: DashboardCuratedScoutRequest[]
  rejected: DashboardCuratedScoutRequest[]
  breakdown: DashboardCuratedScoutRequest[]
  uninterested: DashboardCuratedScoutRequest[]
}

export interface DashboardScoutsAcceptedCuration
  extends DashboardCuratedScoutRequest {
  curationId: number
  curationName: string
}

export type BrokingRequestStatus = 'ACCEPTED' | 'REJECTED' | null

export type BrokingRequestHiringProcessType =
  | 'COFFEE_CHAT'
  | 'FIRST_INTERVIEW'
  | 'OTHER'

// 지원 또는 제안 이력이 있는 프로필은 걸러주기 때문에 응답값이 조금 다름
export interface DashboardInterestedInMeProfileItem
  extends DashboardSimpleScoutProfile {
  hasPendingScoutProposal: boolean
  hasTempScoutProposal: boolean
  saveLists: number[]
  proposalId?: number
  updatedAt: string
  lastActiveAt: string | null
  lastListedAt: string | null
  viewedMeAt: string | null // 인재가 기업 확인
  savedMeAt: string | null // 인재가 기업 저장
  isNew: boolean
  requiredConditions: ScoutRequestDataKeyIsRequired[] | null
  locations: Location[]
  serviceAreas: WithIdName[]
  fundingRounds: WithIdName[]
  memberCount: Range | null
  devCount: Range | null
  salary: Range | null
  miscOptions: ScoutRequestMiscOptions | null
}

export interface DashboardScoutsSummary {
  proposalUnreadMessagesCount: number
  applicationUnreadMessagesCount: number
  unreadMessagesCount: number
  proposalCount: number
  assessmentCount: number
}

export type Progress = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8

export interface WithYearMonth {
  year: string
  month: string
}

export interface EditableEducation extends Omit<EducationHistory, 'school'> {
  name: string | null
  image: string | null
  school: number | null
  backup?: EditableEducation
}

export interface EditableCareer {
  id?: number
  name: string
  company: number | null
  isStartup: boolean
  serviceAreas: ServiceArea[]
  link: string | null
  image: string | null
  position: string
  started: WithYearMonth
  ended: WithYearMonth
  isWorking: boolean
  techStacks: TechStack[]
  description: string
  isVerified: boolean
  isCompleted: boolean // '완료' 하는 시점에 true로 바뀌고, 삭제되지 않는 한 다시 false가 되지 않음
  isCreatedByUser: boolean
  isEdited?: boolean
  backup?: EditableCareer
}

export interface EditableOtherActivity {
  id?: number
  title: string
  description: string
  started: WithYearMonth
  ended: WithYearMonth
  isOnGoing: boolean
  isCompleted: boolean // '완료' 하는 시점에 true로 바뀌고, 삭제되지 않는 한 다시 false가 되지 않음
  techStacks: TechStack[]
  isEdited?: boolean
  backup?: EditableOtherActivity
}

export interface EditableLanguageSkill {
  id?: number
  language: WithIdName | null
  level: WithIdName | null
  description: string
  isCompleted: boolean
  isEdited: boolean // 저장된 적 있는(id 부여됨) 데이터를 수정해서, 다시 저장할 필요가 있는지 (요청서 작성 플로우에서만 사용)
  backup?: EditableLanguageSkill
}

export type ProfileFormPages =
  | 'education'
  | 'introduce'
  | 'expectation'
  | 'career'
  | 'resume'
  | 'language'
  | 'image'
  | 'done'
  | 'job-searching-reason'

export type AutoAnonymizationResult =
  | 'FILE_CREATED'
  | 'FILE_CREATION_FAILED'
  | 'NOT_FOR_ANONYMIZATION'
  | 'UNSUPPORTED_FILE_FORMAT'

export type FileSystemVersion = 1 | 2 // 1은 프로필 첨부자료, 2는 인재 배지 소개자료

export interface AdminFile {
  applicationFileId: NumberOrNull // 프로필 첨부자료
  userFileId: NumberOrNull // 인재 배지 소개자료
  name: string
  link: string
  createdAt: string
  anonymizedKey: string | null
  anonymizedFileCreatedAt: string | null
  anonymizedBy: string | null
  isNotForAnonymization: boolean
  autoAnonymizationResult: AutoAnonymizationResult | null
  autoAnonymizedAt: string | null
  isReviewed: boolean | null
  reviewedBy: string | null
  fileSystemVersion: FileSystemVersion
}

export interface PaginationParams {
  offset: number
  limit: number
}

export interface SearchReqParams extends PaginationParams {
  searchQuery: string
}

export interface AdminFilesReqParams extends PaginationParams {
  showApplicationFiles: boolean
  showUserFiles: boolean
  isReviewed?: boolean
}

export interface IdpCertificate extends WithIdName {
  description: string
  requirement: string
  imageUrl: string
  detailQuestions: WithIdName[]
  traits: CertificateTrait[]
  type: 'file&link' | 'link' | 'text'
  textPlaceholder: string
}

export type CertificateTrait =
  | '자기주도성'
  | '커뮤니케이션'
  | '오너십'
  | '책임감'
  | '실무 능력'
  | '스타트업 이해도'
  | '문제해결력'

export interface UserIdpCertificateDetail {
  id: number
  certText: string | null
  isCertified: boolean
  isContentFilled: boolean
  idpCertificateId: number
  details: {
    questionId: number
    answer: idpCertificateAnswer
  }[]
  certFiles: WithIdLink[]
  certLinks: WithIdLink[]
  introFiles: WithIdLink[]
  introLinks: WithIdLink[]
}

export interface idpCertificateAnswer {
  id: number
  content: string
}

export type CertificateFileType = File | WithIdLink | null

interface IntroQnADetail {
  questionId: number
  answer: string
}

export interface IdpCertificateDetailCommon {
  id: number
  idpCertificateId: number
  details: IntroQnADetail[]
}

export interface DashboardIdpCertificateDetail
  extends IdpCertificateDetailCommon {
  introFiles: AttachmentWithAccessibility[]
  introLinks: AttachmentWithAccessibility[]
}

export interface AdminUserIdpCertificate extends WithIdName {
  certText: string
  isCertified: boolean
  certifiedAt: string
  certifiedBy: string
  createdAt: string
  rejectReason: string
  evaluatedAt: string
  evaluatedBy: string
  phone: string
  idpCertificateId: number
  certFiles: string[]
  certLinks: string[]
  scoutRequestId: number
}

export interface AdminStartup extends WithIdName {
  username: string
  mainEmailPIC: { name: string; position: string; email: string } | null
  remarks: string
  isLocked: boolean
  isSigned: boolean
  isPreInterviewContractSigned: boolean
  isFilledUp: boolean
  isEssential: boolean
  isPublished: boolean
  banReason: string | null // 이용정지 되지 않은 경우 null
  createdAt: string
}

export interface AdminStartupFunnel {
  overview: {
    // 퍼센티지 *100 없이 다 소숫점, 프론트에서 포맷팅(1의자리 반올림)
    target: number // 목표
    current: number // 현재
    onboarding: number // 입점
    scout: number // 스카우트 채용
    application: number // 지원자 채용
  }
  onboarding: AdminStartupBoard[]
  scout: AdminStartupBoard[]
  application: AdminStartupBoard[]
}

export interface AdminStartupBoard {
  name: string // 정식 계정 발급됨
  prevConversion: number // 이전
  aggConversion: number // 누적
  items: AdminStartupItem[]
}

export interface AdminStartupItem extends WithIdName {
  image: string
  joinedAt: string
  currentStatusFrom: string
  lastLoginAt: string
  lastActiveAt: string
  scout: {
    unresponded: number // 미응답채팅
    assessed: number // 1~4점 평가
    interested: number // 3~4점 평가
    proposed: number
    effective: number // 아직 거절/수락되지 않은 유효 제안 개수
    devReplied: number // 회신 받아 채팅한 개수
    confirmedMeets: number
    hired: number
    lastAssessedAt?: string
    lastProposedAt?: string
    lastMsgSentAt: string
  }
  application: {
    unresponded: number // 미응답지원
    positions: number
    applications: number
    resumeAccepted: number
    accepted: number
    hired: number
    lastViewedAt?: string // 지원자 마지막 열람
    lastDecidedAt?: string // 마지막 합불 액션
  }
}

export type ProfileSearchOrderBy =
  | '-idpScore'
  | '-updatedByUserAt'
  | '-relevance'

interface DashboardScoutRequestSearchCommonParams {
  minExperience: number
  maxExperience: number
  minSalary: number
  maxSalary: number
  isEducationCs: boolean
  isEducationFinished: boolean
  isJobInterested: boolean
  hasIntroduction: boolean
  hasAccessibleAttachment: boolean
  shouldMatchTalentStartupRequired: boolean
  keywords: string[]
  jobInterestLevels: number[]
  isNotWorking: boolean
  orderBy: ProfileSearchOrderBy
  isExcludeViewedTalent: boolean // TODO: scout search에 저장되는 값은 아니고, 요청 파라미터로만 사용하는 값. 이 타입을 확장한 DashboardScoutSearchItem 등을 응답값으로 받을 때는 isExcludeViewedTalent 가 없음. 타입 수정 필요
  isRemoteWorkExcluded: boolean
  hasStartupCareerHistory: boolean
}

export interface DashboardScoutRequestSearchFilterParams
  extends WithIdName,
    DashboardScoutRequestSearchCommonParams {
  positions: PositionType[]
  techStacks: WithIdName[]
  workLevels: WithIdName[]
  idpCertificates: IdpCertificate[]
  schoolGroups: SchoolGroup[]
  schools: WithIdName[]
  educationCategories: WithIdName[]
  languageSkills: {
    id?: number
    language: WithIdName
    level: WithIdName | null
  }[]
  locations: WithIdName[]
  fundingRounds: WithIdName[]
  serviceAreas: ServiceArea[]
  careerServiceAreas: ServiceArea[]
  aiAssessmentConditions: string[]
  updatedAt?: string
}

export interface DashboardScoutSearchesParams extends PaginationParams {
  isPositionCuration: boolean // true 일 때만 동작해서 채용공고 추천인재가 아닐 경우 undefined를 보내도 되지만, swr key를 일관되게 관리하기 위해 항상 false 보내기
  startupPositionId?: number // isPositionCuration 이 true 이면 optional, false 이면 undefined
}

export interface DashboardScoutSearchItem
  extends DashboardScoutRequestSearchFilterParams {
  positionCurationInfo: {
    startupPositionId: number
    isNew: boolean
    isPositionPublished: boolean
    isOutdated: boolean
  } | null
}

export interface DashboardScoutRequestOneQueryParams
  extends DashboardScoutRequestSearchCommonParams {
  positions: number[]
  techStacks: number[]
  workLevels: number[]
  idpCertificates: number[]
  schoolGroups: number[]
  schools: number[]
  educationCategories: number[]
  languageSkills: string
  locations: number[]
  fundingRounds: number[]
  serviceAreas: number[]
  careerServiceAreas: number[]
  aiAssessmentConditions: string[]
  searchId?: number
}

export interface DashboardScoutRequestListQueryParams
  extends DashboardScoutRequestOneQueryParams {
  limit: number
  offset: number
}

export interface DashboardScoutSaveProfileListParams {
  saveListId: number
  offset: number
  limit: number
  isNotProposed: boolean
}

export interface ProfileSaveListItem extends WithIdName {
  count: number
  chatCount: number
  newCount: number
}

export interface UserTokenPayload {
  user_id: number
  username: string
  is_fake: boolean
  exp: number
  // TODO(gogo): created_at 지우고 iat 만 유지
  iat?: number // 아직은 ? 처리
  created_at?: string // deprecated => iat 로 대체
}

export interface StartupUserTokenPayload {
  startup_user_id: number
  startup_id: number
  startup_name: string
  is_fake: boolean
  is_admin: boolean
  exp: number
  // TODO(gogo): ditto
  iat?: number
  created_at?: string // deprecated => iat 로 대체
}

export interface StartupInterestedInMe extends StartupForSearch {
  startupPosition:
    | (PositionForList & { userApplication: UserApplicationForPosition })
    | null
}

export interface DashboardInterestedInMeParams extends PaginationParams {
  isSavedMe: boolean
}

export interface AdminPerStartupCurationScoutRequest {
  scoutRequestId: number
  userId: number
  name: string
  experience: number
  workLevel: number
  positions: string[]
  curatedAt: string
  perStartupCurationComment: {
    comment: string | null
    commentedBy: WithIdNameImage | null
  } | null
  commonComment: {
    comment: string | null
    commentedBy: WithIdNameImage | null
  } | null
  isCommonCurated: boolean
  resumeAcceptedAt: string | null
  acceptedAt: string | null
  rejectedAt: string | null
  proposedAt: string | null
  lastViewedAt: string | null
  uninterestedAt: string | null
  uninterestedFeedbackAt: string | null
  uninterestedFeedback: string | null
  brokingRequestedAt: string | null
  memo: string | null
  jobHelperAssessMemo: string | null
  isDeleted: boolean
  deleteReason: string | null
  deletedAt: string | null
  isPublished: boolean
  unPublishedAt: string | null
  uninterestFeedback: string | null
  isBrokingRequestDisabled: boolean
}

export interface DashboardScoutsCuration extends WithIdName {
  count: number
  isHide: boolean
  newCuratedScoutRequestCount: number
  hasNewReject: boolean
  hasNewAcceptance: boolean
  managerName: string
  managerImage: string
  isBrokingRequestDisabled: boolean
  receivedNewCuration: boolean
}

export interface BrokingRequestDetail {
  hiringProcessType: BrokingRequestHiringProcessType
  otherDescription: string | null
  meetLocation: string | null
  contact: string | null
}

export interface UserSearchingReqParams {
  offset?: number
  limit?: number
  searchQuery?: string
  role?: 'admin'
}

export interface ChatItem {
  name: string
  image: string | null
  proposalId: NumberOrNull
  applicationId: NumberOrNull
  lastMessage: ChatMessage | null
  unreadMessagesCount: number
  chatStep?: DashboardChatStatusId
  isRejected?: boolean
  requestId?: NumberOrNull // 기업 사이드만
  startupId?: number // 인재 사이드만
  position?: { name: string; careerType: string }
  status?: ScoutProposalStatus
  statusId?: number
  isCanceled?: boolean
  hasDoneMeet?: boolean
  hasConfirmedMeet?: boolean
  rejectedAt?: string | null
  acceptedAt?: string | null // proposal 일 때 인재가 채팅 수락한 시점
  resumeAcceptedAt?: string | null
  scheduledRejectionDate?: string | null
  shouldBrokingScoutProposal?: boolean // 구직도우미 전용
}

export interface SearchedUser extends WithIdNameImage {
  scoutRequestId: number
  workLevel?: number
  experience?: number
  interestedPositionTypes?: WithIdName[]
}

export interface BaseTrackingInfo {
  id: number
  startup: WithIdName
  user: WithIdName & { phone: string; scoutRequestId: number }
  manager: WithIdNameImage | null
  hireCandidateStatusId: HireCandidateStatus
  lastContact: string // TODO: 제거 예정
  memo: string // TODO: 제거 예정
  hireTrackingMemo: string | null
  deleteReason: string | null
  deletedAt: string | null
  isPublished: boolean
}

export interface AdminTrackingScoutProposal extends BaseTrackingInfo {
  proposedAt: string
  scoutProposalStatusId: ScoutProposalStatus
  acceptedAt: string
  rejectedAt: string
  canceledAt: string
  lastMessagedAt: string
  chatMemo: string
  isCheckNeeded: boolean
  chatMessagesCheckedAt: string
}

export interface AdminTrackingManualMatch extends BaseTrackingInfo {
  createdAt: string
}

export interface AdminTrackingHire extends BaseTrackingInfo {
  hiredAt: string
  hasContractConfirmed: boolean
  manualMatchId?: number
  scoutProposalId?: number
  positionApplicationId?: number
}

export interface AdminTrackingPositionApplication extends BaseTrackingInfo {
  appliedAt: string
  lastViewedAt: string
  applicationStatusId: number
  resumeAcceptedAt: string | null
  rejectedAt: string | null
  acceptedAt: string | null
  chatMemo: string
  chatMessagesCheckedAt: string
}

export type HireCandidateStatus =
  | 'TRACKING'
  | 'POSSIBLE'
  | 'IMPOSSIBLE'
  | 'HIRED'

export type ScoutProposalStatus =
  | 'WAITING'
  | 'ACCEPTED'
  | 'CANCELED'
  | 'REJECTED'
  | 'SUCCEEDED'

export enum HireCandidateStatusMapping {
  TRACKING = '⚪️ 추적중',
  POSSIBLE = '🟢 가능성',
  HIRED = '🔵 채용 확정',
  IMPOSSIBLE = '⚫️ 결렬',
}

export enum ScoutProposalStatusMapping {
  WAITING = '대기',
  ACCEPTED = '수락',
  CANCELED = '취소',
  REJECTED = '거절',
  SUCCEEDED = '합격',
}

export interface BaseTableColumn {
  id: string
  label: string
  minWidth?: number
  align?: 'left' | 'center' | 'right' | 'justify' | 'inherit'
}

export interface AdminStatusInfo {
  hireCandidateStatus: HireCandidateStatus[]
  applicationStatus: WithIdName[]
  scoutProposalStatus: ScoutProposalStatus[]
  perStartupCurationStatus: AdminPerStartupCurationStatusType[]
  perStartupCurationDropReason: AdminPerStartupCurationDropReasonType[]
}

export interface BaseTrackingTableParams {
  userIds: number[]
  startupIds: number[]
  managerIds: number[]
  hireCandidateStatusIds: string[]
}

export type AdminTrackingManualMatchParams = BaseTrackingTableParams

export type ScoutProposalOrderBy = '-lastMessagedAt' | '-proposedAt'

export type ApplicationOrderBy = '-resumeAcceptedAt' | '-appliedAt'

export interface AdminTrackingScoutProposalParams
  extends BaseTrackingTableParams {
  orderBy: ScoutProposalOrderBy
  scoutProposalStatusIds: string[]
}

export interface AdminTrackingApplicationParams
  extends BaseTrackingTableParams {
  orderBy: ApplicationOrderBy
  applicationStatusIds: number[]
}

export interface AdminHireCandidatePutRequestParams {
  manualMatchId?: number
  scoutProposalId?: number
  positionApplicationId?: number
  isCheckNeeded?: boolean
  chatMemo?: string
  managerId?: NumberOrNull
  hireCandidateStatusId?: string
}

export interface AdminChatMessage {
  content: string | null
  messageType: ChatMessageType
  sentAt: string
  sentBy: string
}

export interface TrackingHireBetaFilterParams {
  isLocked?: boolean
  hasInProgress?: boolean
  alertLevels?: TrackingHireAlertLevel
  isHide?: boolean
  isSelfReported?: boolean
  overWeeks?: number
}

export type TrackingStatusFilterName =
  | 'Level 1'
  | 'Level 2'
  | '운영팀 직접 연락'
  | '숨김 처리한 기업'

export type TrackingHireAlertLevel = 1 | 2 | 3

export interface TrackingStatusFilterOption {
  alertLevel: TrackingHireAlertLevel | null
  isHide: boolean
}

export interface TrackingHireStatusInfo {
  filterOption: TrackingStatusFilterOption
  count: number
}

export type TrackingHireAlertReply = 'confirm' | 'inProgress' | 'breakdown'

export enum TrackingHireAlertReplyMapping {
  confirm = '채용자 O',
  inProgress = '진행중',
  breakdown = '모두 결렬',
}

// 어드민 채용추적 페이지에서 쓰이는 채팅 상태
export enum AdminMatchingListChatStatus {
  INPROGRESS = 'INPROGRESS', // DashboardChatStatusType.INPROGRESS 와 달리, 수락전(WAITING) 상태는 포함하지 않음
  CONTACTLATER = 'CONTACTLATER',
  BREAKDOWN = 'BREAKDOWN',
  SUCCEEDED = 'SUCCEEDED',
  WAITING = 'WAITING', // DashboardChatStatusType에는 없는 상태
}

export enum AdminMatchingListChatStatusMapping {
  INPROGRESS = '진행중',
  CONTACTLATER = '추후 연락',
  BREAKDOWN = '결렬',
  SUCCEEDED = '최종 합격',
  WAITING = '수락전',
}

export interface MatchingListUserItem {
  proposalId: NumberOrNull
  applicationId: NumberOrNull
  chatStepUpdatedAt: string | null
  lastMessageSentAt: string
  userName: string
  hireCandidateStatusId: HireCandidateStatus
  chatMemo: string | null
  deletedAt: string | null
  isPublished: boolean
}

export interface MatchingListPerChatStep {
  chatStep: AdminMatchingListChatStatus
  count: number
  userList: MatchingListUserItem[]
}

export interface TrackingHireStartup {
  startupId: number
  startupName: string
  isSigned: boolean
  memo: string | null
  lastActiveAt: string | null
  isSelfReported: boolean
  lockElapsedDay: NumberOrNull
  isHide: boolean
  alertStatus: {
    notifiedAt: string
    alertLevel: TrackingHireAlertLevel
    alertReply: TrackingHireAlertReply | null
  } | null
  answerSucceededStep: boolean | null
  matchingList: MatchingListPerChatStep[]
}

export interface TrackingHireStartupSimple {
  name: string
  isSigned: boolean
  alertLevel: TrackingHireAlertLevel | null
  alertReply: TrackingHireAlertReply | null
  isHide: boolean
  hasInProgress: boolean
  lockElapsedDay: NumberOrNull
  lastActiveAt: string | null
  memo: string | null
  chatStep: { id: AdminMatchingListChatStatus; count: number }[]
  hireCandidateStatus: { id: HireCandidateStatus; count: number }[]
}

export interface TrackingHireUserListFilterParams {
  chatStep: AdminMatchingListChatStatus[]
  hireCandidateStatus: HireCandidateStatus[]
  orderBy?: 'lastMessageSentAt' // 파라미터 안 보내면 기본값은 내림차순(최신순)
}

export interface MatchingListStartupItem {
  proposalId: NumberOrNull
  applicationId: NumberOrNull
  startupId: number
  startupName: string
  chatStep: AdminMatchingListChatStatus
  chatStepUpdatedAt: string | null
  hireCandidateStatusId: HireCandidateStatus
  chatMemo: string | null
}

export interface TrackingHireMatchedUser {
  userId: number
  userName: string
  scoutRequestId: number
  proposalId: number | null
  applicationId: number | null
  phone: string
  memo: string | null
  chatStep: AdminMatchingListChatStatus
  chatStepUpdatedAt: string | null
  lastMessageSentAt: string | null // 예전 서류합격 건은 기획 변경 후 채팅방 만들어졌으나 메시지 없을 수 있음
  hireCandidateStatusId: HireCandidateStatus
  chatMemo: string | null
  deletedAt: string | null
  matchingList: MatchingListStartupItem[]
  isPublished?: boolean
}

export interface AdminPerStartupCuration extends WithIdName {
  position: WithIdName
  startup: WithIdName & { proactivityScore: number }
  manager: WithIdNameImage | null
  status: AdminPerStartupCurationStatusType
  curationRequestedAt: string
  statusUpdatedAt: string
  isUrgent: boolean
  uninterestedCount: number
  brokingRequestedCount: number
  rejectedCount: number
  isAutoCreated: boolean
  scoutRequestCount: number
  viewedScoutRequestCount: number
  positionLink: string
  hasChecked: boolean
  createdAt: string
  curationResolvedAt: string
}

export interface AdminPerStartupClosedCuration {
  hired: AdminPerStartupClosedCurationItem[]
  insufficientTalent: AdminPerStartupClosedCurationItem[]
  hiredOnOtherPlatform: AdminPerStartupClosedCurationItem[]
  startupDeclined: AdminPerStartupClosedCurationItem[]
  startupNoResponse: AdminPerStartupClosedCurationItem[]
}

export interface AdminPerStartupClosedCurationItem extends WithIdName {
  position: WithIdName
  startup: WithIdName
  manager: WithIdNameImage | null
  status: AdminPerStartupCurationStatusType
  statusUpdatedAt: string
}

export interface AdminPerStartupCurationDetail extends WithIdName {
  position: WithIdName
  startup: WithIdName & {
    proactivityScore: number
    remarks: string | null
  }
  positionLink: string | null
  memo: string | null
  manager: WithIdNameImage | null
  isUrgent: boolean
  isBrokingRequestDisabled: boolean
  curationFeedback: string | null
}

export interface Comment {
  comment: string | null
  commentedBy: WithIdNameImage | null
}

export enum AdminPerStartupCurationStatusMapping {
  NEED_CURATION = '신규 추천 요청',
  NEED_FEEDBACK = '처리 대기',
  NEED_MATCHING = '재추천 필요',
  MATCHED = '연결 진행 중',
  DROPPED = '취소/포기',
  HIRED = '채용 성사',
}

export type AdminPerStartupCurationStatusType =
  | 'NEED_CURATION'
  | 'NEED_FEEDBACK'
  | 'NEED_MATCHING'
  | 'MATCHED'
  | 'DROPPED'
  | 'HIRED'

export interface ScoutPreAnalysis {
  similarTalentProposedCount: number
  startupsSeekingMeCount: number
}

export interface CurationStatus {
  curatedAt: string | null
  brokingRequestedAt: string | null
  uninterestedAt: string | null
  uninterestFeedback: string | null
  uninterestFeedbackAt: string | null
  brokingRequestStatus: BrokingRequestStatus
  proposedAt: string | null
  rejectedAt: string | null
  acceptedAt: string | null
  brokingRequestStatusUpdatedAt: string | null
  brokenAt: string | null
  canceledAt: string | null
  userAcceptedAt: string | null
  isBrokingRequestDisabled: boolean
  statusUpdatedAt: string | null
  lastViewedAt: string | null
}

export type NumberOrNull = number | null

export interface WithIdNameImage extends WithIdName {
  image: string | null
}

export interface SelectedChat {
  proposalId: NumberOrNull
  applicationId: NumberOrNull
}

export interface SucceededChat extends SelectedChat {
  name: string
  image: string | null
  workStartDate: string
}

export interface DashboardChatStatusPayload {
  proposalId: NumberOrNull
  applicationId: NumberOrNull
  chatStep: DashboardChatStatusId
}

export interface ApplicationsAcceptPayload {
  applicationId: number
  workStartDate: string | null
}

export interface ProposalsHirePayload {
  proposalId: number
  workStartDate: string | null
}
export interface UserAccount {
  username: string
  name: string
  phone: string
  email: string
  isAdmin: boolean
  experience: string
  positions: PositionType[]
  techStacks: TechStack[]
  shouldGenerateScoutRequest: boolean
}

export enum DashboardChatStatusType {
  INPROGRESS = 'INPROGRESS',
  CONTACTLATER = 'CONTACTLATER',
  BREAKDOWN = 'BREAKDOWN',
  SUCCEEDED = 'SUCCEEDED',
}

// TODO: 타입 리팩토링
export type DashboardChatStatusId =
  | DashboardChatStatusType.INPROGRESS
  | DashboardChatStatusType.CONTACTLATER
  | DashboardChatStatusType.BREAKDOWN
  | DashboardChatStatusType.SUCCEEDED

export enum DashboardChatStatusIdMapping {
  INPROGRESS = '진행중',
  CONTACTLATER = '추후 연락',
  BREAKDOWN = '결렬',
  SUCCEEDED = '최종 합격',
}

export interface DashboardChatStatus {
  id: DashboardChatStatusId
  count: number
  hasUnreadMessage: boolean
}

export type HidePenaltyNoticeType =
  | 'application'
  | 'proposal'
  | 'chatStatusClearStep'

export interface DashboardScoutsTextToSearch {
  id: number
  text: string
  explain: string
  inputSearch: DashboardScoutRequestSearchFilterParams
  outputSearch: DashboardScoutRequestSearchFilterParams
  createdAt: string
  updatedAt: string
}

export interface DashboardScoutsSearchOptions {
  schoolGroups: SchoolGroup[]
}

export interface DashboardScoutsSearchInfo {
  id: number
  positionId?: number
  name: string
  updatedAt: string
  isNew: boolean | null // position curation 일 때만 boolean 값 있음
  isPositionPublished: boolean | null // position curation 일 때만 boolean 값 있음
}

export interface ProposalReqParams {
  limit?: number
  offset?: number
  userName?: string
  saveLists?: number[]
  chatStep?: DashboardChatStatusId
}

export type ScoutRequestAiAssessmentSectionType =
  | 'QUALIFICATION'
  | 'PREFERRED'
  | 'CUSTOM'

export interface ScoutRequestAiAssessmentSection {
  description: string
  type: ScoutRequestAiAssessmentSectionType
  maxScore: number
  score: number
  reason: string
}

export interface ScoutRequestAiAssessment {
  sessionId: number
  totalScore: number
  summary: string
  sections: ScoutRequestAiAssessmentSection[]
}

export type ScoutProfileDraftType =
  | 'education'
  | 'career'
  | 'languageSkill'
  | 'introduction'
  | 'expectation'
  | 'jobSearchingReason'

export enum ScoutRequestFilterAssessmentSectionsEnum {
  isActivelySeeker = '적극 구직 중',
  hasStartupCareerHistory = '스타트업 근무 경험',
  isEducationCs = '컴퓨터 관련전공',
  hasIntroduction = '자기소개 있음',
  hasAccessibleAttachment = '공개된 첨부 자료 있음',
  shouldMatchTalentStartupRequired = '필수 조건 부합',
  isNotWorking = '재직중 제외',
  isRemoteWorkExcluded = '원격근무만 가능 제외',
  isEducationFinished = '졸업/졸업예정',
  jobInterestLevels = '직무 관심 수준',
  workLevels = '경력',
  educationCategories = '최종학력',
  schools = '출신 학교',
  locations = '희망 근무 지역',
  fundingRounds = '인재 선호 투자 단계',
  positions = '포지션',
  languageSkills = '외국어 능력',
  idpCertificates = '보유한 인재 배지',
  techStacks = '스킬',
  serviceAreas = '관심 서비스 분야',
  careerServiceAreas = '경력 서비스 분야',
  experience = '경력 연차',
  salary = '연봉',
  keywords = '키워드',
}

export type ScoutRequestFilterAssessmentSectionKey =
  keyof typeof ScoutRequestFilterAssessmentSectionsEnum

export interface ScoutRequestFilterAssessmentSections {
  isActivelySeeker?: boolean
  hasStartupCareerHistory?: boolean
  isEducationCs?: boolean
  hasIntroduction?: boolean
  hasAccessibleAttachment?: boolean
  shouldMatchTalentStartupRequired?: boolean
  isNotWorking?: boolean
  isRemoteWorkExcluded?: boolean
  isEducationFinished?: boolean
  jobInterestLevels?: boolean
  workLevels?: boolean
  educationCategories?: boolean
  schools?: boolean
  locations?: boolean
  fundingRounds?: boolean
  positions?: WithTotalMatched
  languageSkills?: WithTotalMatched
  idpCertificates?: WithTotalMatched
  techStacks?: WithTotalMatched
  serviceAreas?: WithTotalMatched
  careerServiceAreas?: WithTotalMatched
  experience?: boolean
  salary?: boolean
  keywords?: boolean
}

export interface WithTotalMatched {
  total: number
  matched: number
}

export interface ScoutRequestFilterAssessment {
  score: number
  sections: ScoutRequestFilterAssessmentSections
}

export interface ApplicationParams {
  files?: number[]
  links?: string[]
  fitReason?: string | null
  joinReason?: string | null
  fulfillments?: ApplicationFulfillmentParams[]
}

export interface ApplicationFulfillmentParams {
  conditionId: number
  description: string | null
  isFit: boolean
}

export interface ResumeAnalysisCharacterMatch {
  score: number
  jobPostingSummary: string
  conditionFulfillment: {
    condition: string
    detail: string
    fulfillmentIndex: number // 0.0~1.0
  }[]
  positiveFeedback: string
  negativeFeedback: string
}

export interface ResumeAnalysisFactAssault {
  input: {
    businessScale: string
    serviceAreas: string[]
    positionType: string
    experience: number
  }
  output: {
    career: { title: string; content: string }
    performance: { title: string; content: string }
    skills: { title: string; content: string }
    introduction: { title: string; content: string }
    overall: { title: string; content: string }
  }
}

export interface ResumeAnalysisKeywordsMatch {
  resumeKeywords: {
    keyword: string
    importanceIndex: number
  }[]
  resumeSummary: string
  jobPostingKeywords: {
    keyword: string
    importanceIndex: number
  }[]
  jobPostingSummary: string
  companyNeeds: string
  matchingKeywords: string[]
  totalFeedBack: string
}

export type DashboardScoutsRecommendedTabType =
  | 'newCurated'
  | 'waiting'
  | 'accepted'
  | 'rejected'
  | 'canceled'

export type PositionFilterType = keyof StartupPositionFilterParams

export type ScoutSearchSessionEventType =
  | 'SAVE'
  | 'PROPOSE'
  | 'MEMO'
  | 'VIEW_PROFILE_DETAIL'
  | 'CANCEL_AI_ASSESSMENT'
  | 'OPEN_PROFILE_IN_NEW_TAB'

export interface StartupPositionCurationMe {
  feedback?: { isNeeded: boolean; isExists: boolean }
  items: MyCurationStartupPosition[]
}

export interface MyCurationStartupPosition {
  id: number
  startup: {
    id: number
    name: string
    thumbnail: string
    createdAt: string
    memberCount: number
    fundingRound: string | null
    location: string
    tags: string[]
    serviceAreas: string[]
    briefIntro: string
    isPublished: boolean
    startupPosition: {
      id: number
      name: string
      careerType: WithIdName
      experienceRange: Range | null
      publishedAt: string
      averageReplyPeriod: NumberOrNull
      isSincerityReview: boolean
      techStacks: string[]
      isPublished: boolean
    }
  }
  fitConditions: string[]
  positionFitReason: string[]
  motivationFitReason: string[]
  criteria: string[]
  curatedAt: string
  status: InterestType
  hasDraftMessage: boolean
  lastViewedAt: string | null
}

export type InterestType = 'INTERESTED' | 'UNINTERESTED' | 'NOT_DECIDED'

export type AdminBrokingRequestType =
  | 'unhandled'
  | 'planningMeetings'
  | 'beforeMeetings'
  | 'afterMeetings'
  | 'canceled'
  | 'accepted'

// meetingInfo 부분을 따로 분리
export interface AdminBrokingRequestMeetingInfo {
  meetDate: string
  timeMemo: string
  location: string
  remark: string
}

export type AdminBrokingRequestInterestedType =
  | 'READ_NOT_DECIDED'
  | 'NOT_READ'
  | 'INTERESTED'
  | 'NOT_INTERESTED'
  | 'NOT_DECIDED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'LEFT_CHAT'

export enum AdminBrokingRequestInterestedTypeMapping {
  READ_NOT_DECIDED = '읽고 처리안함',
  NOT_READ = '안읽음',
  INTERESTED = '관심있어요',
  NOT_INTERESTED = '관심없어요',
  NOT_DECIDED = '제안 처리안함',
  ACCEPTED = '제안 수락',
  REJECTED = '제안 거절',
  LEFT_CHAT = '채팅방 나감',
}

export type AdminBrokingRequestMeetingType =
  | 'DIRECT_MEET'
  | 'DELEGATED_MEET'
  | 'STARTUP_PROPOSE'

export enum AdminBrokingRequestMeetingTypeMapping {
  DIRECT_MEET = '직접 미팅',
  DELEGATED_MEET = '대신미팅 요청',
  STARTUP_PROPOSE = '기업 제안',
}

export interface AdminBrokingRequestHireProcess
  extends Omit<BrokingRequestDetail, 'hiringProcessType'> {
  type: BrokingRequestHiringProcessType
}

export interface AdminBrokingRequestItem {
  id: number
  startup: WithIdName
  curationId: number
  curationName: string
  user: WithIdName & { phone: string; lastActiveAt: string }
  meetingType: AdminBrokingRequestMeetingType
  reason: string | null
  hiringProcess: AdminBrokingRequestHireProcess | null
  dDay: number
  unreadMessages: number
  manager: WithIdNameImage | null
  remindMemo: string
  userMemo: string
  isPinned: boolean
  pinnedAt: string | null
  meetingInfo: AdminBrokingRequestMeetingInfo
  createdAt: string
  status: AdminBrokingRequestInterestedType
  proposalId: number
  scoutRequestId: number
  hiredAt: string | null
  rejectedAt: string | null
  remindMeetingAt: string | null
  hireTrackingMessageAt: string | null
}

// 전체 data 구조endedAt
export interface AdminBrokingRequests {
  unhandled: AdminBrokingRequestItem[]
  planningMeetings: AdminBrokingRequestItem[]
  beforeMeetings: AdminBrokingRequestItem[]
  afterMeetings: AdminBrokingRequestItem[]
}

export interface AdminBrokingRequestsConfirmed {
  canceled: AdminBrokingRequestItem[]
  accepted: AdminBrokingRequestItem[]
}

export interface AdminBrokingRequestsParams {
  managerId?: number[]
  startupId?: number[]
  userId?: number[]
  meetingType?: AdminBrokingRequestMeetingType[]
  interestedType?: AdminBrokingRequestInterestedType[]
  startedAt?: string
  endedAt?: string
}

export enum PositionFilterTitleType {
  POSITION = 'position',
  EXPERIENCE = 'experience',
  TECH_STACK = 'techStack',
  SALARY = 'salary',
  LOCATION = 'location',
  SERVICE_AREA = 'serviceArea',
  COMPANY_SCALE = 'companyScale',
  KEYWORD = 'keyword',
}

export interface StartupPositionCurationFeedbackInfo {
  reasons: {
    negative: WithIdName[]
    positiveNotApplied: WithIdName[]
  }
}

export enum StartupPositionCurationFeedbackType {
  POSITIVE_NOT_APPLIED = 'POSITIVE_NOT_APPLIED',
  NEGATIVE = 'NEGATIVE',
  DECLINED = 'DECLINED',
}

export interface PreInterviewQuestion {
  id: number
  content: string
  category: WithIdName
}

export interface AdminPreInterviewItem {
  id: number
  scoutRequestId: number
  user: { name: string; phone: string }
  startup: WithIdName & { isPreInterviewContractSigned: boolean }
  questions: PreInterviewQuestion[]
  summary: string | null
  videoLink: string | null
  reportLink: string | null
  requestedAt: string
  resolvedAt: string | null
  failedAt: string | null
  failReason: string | null
}

export interface DashboardApplicationPreInterviewCustomQuestion {
  id: NumberOrNull
  content: string
  action: MethodType
}

export enum MethodType {
  CREATE = 'CREATE',
  DELETE = 'DELETE',
  UPDATE = 'UPDATE',
}

export interface AdminActiveStartupPositionStartup {
  id: number
  name: string
  createdAt: string
  lastActiveAt: string
  proposalCount: number
  curationProposalCount: number
  managerCurationCount: number
  managerCurationSrCount: number
  managerCurationSrReactionCount: number
}

export interface AdminActiveStartupPosition {
  id: number
  name: string
  publishedAt: string
  applicantsCount: number
  proposalsCount: number
  scoutSearchCount: number
  scoutSearchNotiHistoryCount: number
  scoutSearchViewHistoryCount: number
  startup: AdminActiveStartupPositionStartup
}

export interface AdminActiveHireNeeds {
  positions: AdminActiveStartupPosition[]
}

export interface DashboardPositionAdvertisementsParams {
  type: 'advertiseStartup' | 'activelyHire'
  targetUserCount: number
  amount: number
  period: number
}

export interface DashboardPositionsInfo {
  ads: {
    advertiseStartup: {
      targetUserCount: number
      amount: number
      period: number
    }[]
    activelyHire: {
      targetUserCount: number
      amount: number
      period: number
    }[]
    latestOption: {
      advertiseStartup: {
        isChecked: boolean
        period: number
      }
      activelyHire: {
        isChecked: boolean
        period: number
      }
    }
  }
}
export interface AdminPerStartupCurationParams {
  startupIds: number[]
  managerIds: number[]
  startedAt: string
  endedAt: string
  isUrgent: boolean
  hasChecked: boolean
  proactivityScores: number
}

export interface AdminPerStartupClosedCurationParams {
  startupIds: number[]
  managerIds: number[]
  startedAt: string
  endedAt: string
}

export type AdminPerStartupCurationDropReasonType =
  | 'INSUFFICIENT_TALENT'
  | 'HIRED_ON_OTHER_PLATFORM'
  | 'STARTUP_DECLINED'
  | 'STARTUP_NO_RESPONSE'
  | 'HIRED'

export enum AdminPerStartupCurationDropReasonEnum {
  HIRED = '채용 성사',
  INSUFFICIENT_TALENT = '인재 부족',
  HIRED_ON_OTHER_PLATFORM = '다른곳 채용마감',
  STARTUP_DECLINED = '기업 거부',
  STARTUP_NO_RESPONSE = '기업 무응답',
}

export interface AdminPerStartupCurationCreateParams {
  startupId: number
  managerId: number
  positionId: number
  name: string
  isUrgent: boolean
  proactivityScore: number
  remarks?: string // 기업 메모
  memo?: string // TO 메모
  positionLink?: string
}

export interface AdminPerStartupCurationUpdateParams {
  startupId?: number
  managerId?: number
  positionId?: number
  memo?: string
  name?: string
  status?: AdminPerStartupCurationStatusType
  dropReason?: AdminPerStartupCurationDropReasonType
  isUrgent?: boolean
  proactivityScore?: number
  positionLink?: string
  isRestore?: boolean
  isBrokingRequestDisabled?: boolean
}

export interface ScoutSearchCandidate {
  scoutRequestId: number
  name: string
  image: string
  experience: number
  isProposed: boolean
}

export enum WorkLevelType {
  INTERN = 'INTERN',
  JUNIOR = 'JUNIOR',
  SENIOR = 'SENIOR',
}

// 구직자쪽 데이터
export enum WorkLevelId {
  INTERN = 1,
  JUNIOR = 2,
  SENIOR = 3,
}

// 기업쪽 데이터
export enum CareerTypeId {
  JUNIOR = 1,
  SENIOR = 2,
  ENTIRE = 3,
  INTERN = 4,
}

export enum JobInterestLevelId {
  ONLY_INTERN = 1, // 인턴 - 인턴만 희망
  CONSIDER_FULL_TIME = 2, // 인턴 - 인턴 후 정규직 고려
  DESIRE_FULL_TIME = 3, // 인턴 - 꼭 정규직 희망
  WORKING_LEVEL = 10, // 경력 - 실무자 희망
  TEAM_LEAD = 11, // 경력 - 팀장급 희망
  C_LEVEL = 12, // 경력 - C 레벨 희망
}

export enum ScoutProposalSelectAnswerEnum {
  SEEKING_JOB = 'SEEKING_JOB', // 구직 중이에요
  MEET_FOR_HIRE = 'MEET_FOR_HIRE', // 채용 목적의 미팅도 괜찮아요 (구직 중이고 관심있음)
  NEED_MORE_INFO = 'NEED_MORE_INFO', // 아직 고민되는 부분이 있어요
  NOT_SEEKING_JOB = 'NOT_SEEKING_JOB', // 딱히 구직을 하고있지 않아요
  PASSIVE_MEET_FOR_HIRE = 'PASSIVE_MEET_FOR_HIRE', // 채용 목적의 미팅도 괜찮아요 (구직은 안하지만 관심있음)
  MEETING_FOR_SMALL_TALK = 'MEETING_FOR_SMALL_TALK', // 가벼운 커피챗만 원해요
  SEEKING_JOB_AGAIN = 'SEEKING_JOB_AGAIN', // 다시 구직 시작했어요
}

export enum ProfileCategoryId {
  USER_INFO = 'userInfo',
  CAREER = 'career',
  EDUCATION = 'education',
  LANGUAGE_SKILL = 'languageSkill',
  INTRODUCTION = 'introduction',
  EXPECTATION = 'expectation',
  JOB_SEARCHING_REASON = 'jobSearchingReason',
  RESUME = 'resume',
  CONDITION = 'condition',
  IDP_CERTIFICATION = 'idpCertification',
}

export interface MyScoutProfileAnalysis {
  id: number
  strengths: WithTitleContent[]
  weaknesses: WithTitleContent[]
  howToUpgrade: WithTitleContent[]
  startups: {
    id: number
    name: string
    image: string
    startupPosition: {
      id: number
      name: string
      careerType: string
      matchingRate: number
    }
  }[]
}

export interface WithTitleContent {
  title: string
  content: string
}

export interface MyProfileAnalysisFeedback {
  expired: NumberOrNull
  ignoredCount: number
  isCompletedFeedback: boolean
  isCompletedReanalysisRequested: boolean
}

export interface PositionForAnalysisResult extends WithIdName {
  isPublished: boolean
  startup: WithIdName & { thumbnail: string }
  userApplication: UserApplicationForPosition
}

export interface StartupPositionAnalysis {
  position: PositionForAnalysisResult
  aiAssessment: Omit<ScoutRequestAiAssessment, 'sessionId'>
  createdAt: string
  viewedAt: string | null
}

export enum AnalysisStatusCode {
  COMPLETED = 200, // 분석 완료
  AVAILABLE = 201, // 분석 요청 가능
  IN_PROGRESS = 202, // 분석 중
  FAILED = 206, // 분석 실패
  NEED_LOGIN = 401, // 로그인 필요
}

export interface ActivelyHireStartupAd {
  id: number
  startup: WithIdName | null
  title: string
  subTitle: string
  description: string
  thumbnail: string
  targetUrl: string
  order: number
}

export interface SalaryHistory {
  year: string
  amount: number
}
export interface ScoutRequestMyInfoFillStatus {
  completedCount: number
  status: 'AVAILABLE' | 'NOT_AVAILABLE'
  hasInfoFillRequest: boolean // 첫 대신 작성 요청 여부
  hasCompletedRequest: boolean // 첫 대신 작성 완료 여부
  infoFillCompletedAt: string // 첫 대신 작성 완료 시점
}

export interface ScoutRequestExampleHighlight {
  id: number
  title: string
  excerpt: string
  description: string
}

export interface ScoutRequestExample {
  id: number
  scoutRequest: ScoutRequestForExample
  achievement: string
  strength: string
  highlights: ScoutRequestExampleHighlight[]
}

export interface ScoutRealTimeStatus {
  startups: number
  proposals: number
  chatMessages: number
  openedProfiles: number // 열람된 프로필 수
  newProfiles: number
  totalProfiles: number // 나머지는 모두 최근 한달 내 수치이고, 이것만 전체 기간
  resumeAcceptances: number
  acceptedProposals: number
  signups: number
}
